import type { ReactNode } from 'react';
import type { Id, ToastOptions } from 'react-toastify';
import { toast } from 'react-toastify';

import type { AlertState } from './alert';
import { Alert } from './alert';

/**
 * How long a toast stays on screen, the same for every state. Every state closes
 * on its own: a toast the person has to click away stays forever, and because the
 * container lives outside the app tree (see ToastHost) it also followed them onto
 * the next page. The window is short because it is not the only safeguard — the
 * container pauses the countdown while the pointer is over the toast and while
 * the tab is out of focus, so a slow reader is never cut off mid-sentence.
 *
 * ToastHost passes this to the container as well, so a direct toast() call could
 * not end up with a different duration.
 */
export const AUTO_CLOSE_MS = 5000;

export interface NotifyOptions extends ToastOptions {
  /**
   * Keep this toast when the route changes. Only for a message raised right
   * before a redirect, where the confirmation or the failure belongs to the
   * page being navigated to — the SSO callback hop, or a checkout success that
   * lands on /thanks-for-purchase. Everything else is page-scoped: it is about
   * the screen that raised it, so it goes away with that screen. Even a kept
   * toast still closes on its own timer; this only exempts it from the
   * route-change sweep.
   */
  keepOnNavigate?: boolean;
}

// Ids of the toasts that belong to the current page. Cleared on every route
// change, so it only ever holds one page's worth.
const pageScoped = new Set<Id>();

function show(state: AlertState, message: ReactNode, options?: NotifyOptions) {
  const { keepOnNavigate = false, ...toastOptions } = options ?? {};

  const id = toast(({ closeToast }) => <Alert state={state} message={message} onClose={closeToast} />, { autoClose: AUTO_CLOSE_MS, ...toastOptions });

  if (!keepOnNavigate) pageScoped.add(id);

  return id;
}

export const notify = {
  success: (message: ReactNode, options?: NotifyOptions) => show('success', message, options),
  error: (message: ReactNode, options?: NotifyOptions) => show('error', message, options),
  warning: (message: ReactNode, options?: NotifyOptions) => show('warning', message, options),
  info: (message: ReactNode, options?: NotifyOptions) => show('info', message, options),
  dismiss: (toastId?: Id) => {
    if (toastId === undefined) pageScoped.clear();
    else pageScoped.delete(toastId);
    toast.dismiss(toastId);
  },
  /** Drops the toasts raised by the page being left. Called by ToastHost on every route change. */
  dismissPageScoped: () => {
    pageScoped.forEach((id) => toast.dismiss(id));
    pageScoped.clear();
  },
};

/** Ready-made option for the call sites that raise a toast and then redirect. */
export const KEEP_ON_NAVIGATE: NotifyOptions = { keepOnNavigate: true };
